import type { ComponentType } from 'react';
import * as THREE from 'three';
import { CLASSICAL, INDUSTRIAL, MODERN } from '../materials';
import { Beam, gableRoof, type ModelProps, Pole, pyramid, Smoke } from '../parts';

const { WOOD, DARK_WOOD, RACE_WATER, FOREST_PINE, PLANK, PLASTER } = CLASSICAL;
const { BRICK, DARK_BRICK, IRON, STEEL, SOOT, TRACTOR, TYRE } = INDUSTRIAL;
const { CONCRETE, GLASS } = MODERN;

// Pines the size and colour of the map's forest trees (TileDecorations): base on the ground.
const pine = new THREE.ConeGeometry(0.18, 0.5, 6).translate(0, 0.25, 0);

const G = {
  pine,
  trunk: new THREE.CylinderGeometry(0.03, 0.035, 0.06, 5),
  stump: new THREE.CylinderGeometry(0.045, 0.05, 0.05, 7),
  log: new THREE.CylinderGeometry(0.035, 0.035, 0.28, 7).rotateZ(Math.PI / 2),
  hut: new THREE.BoxGeometry(0.2, 0.15, 0.18),
  hutRoof: pyramid(0.18, 0.1),
  // L2 yard
  sawhorseLeg: new THREE.BoxGeometry(0.018, 0.14, 0.018),
  sawhorseBar: new THREE.BoxGeometry(0.2, 0.02, 0.02),
  cartBed: new THREE.BoxGeometry(0.1, 0.04, 0.16),
  cartWheel: new THREE.CylinderGeometry(0.05, 0.05, 0.02, 10).rotateZ(Math.PI / 2),
  cartHandle: new THREE.BoxGeometry(0.012, 0.012, 0.1),
  cartLog: new THREE.CylinderGeometry(0.025, 0.025, 0.16, 6).rotateX(Math.PI / 2),
  // The sawpit shed: an open lean-to over a pit with a log across it.
  pitRoof: new THREE.BoxGeometry(0.34, 0.02, 0.26),
  pit: new THREE.BoxGeometry(0.1, 0.012, 0.18),
  trestle: new THREE.BoxGeometry(0.1, 0.05, 0.03),
  // L3 sawmill: a timber mill house with its ridge along x (the broad roof
  // slope faces the camera), a big light water wheel on its front over the race.
  millHouse: new THREE.BoxGeometry(0.34, 0.18, 0.2),
  millRoof: new THREE.CylinderGeometry(0.17, 0.17, 0.42, 3)
    .rotateZ(Math.PI / 2)
    .rotateX(-Math.PI / 2),
  millDoor: new THREE.BoxGeometry(0.07, 0.1, 0.01),
  wheel: new THREE.CylinderGeometry(0.12, 0.12, 0.03, 16).rotateX(Math.PI / 2),
  hub: new THREE.CylinderGeometry(0.03, 0.03, 0.05, 8).rotateX(Math.PI / 2),
  paddle: new THREE.BoxGeometry(0.05, 0.022, 0.06),
  // The millrace: a bright water channel along x in front of the mill.
  race: new THREE.BoxGeometry(0.4, 0.016, 0.1),
  raceBank: new THREE.BoxGeometry(0.4, 0.03, 0.02),
  // L3 log deck: a long pile of logs along x behind the mill.
  deckLog: new THREE.CylinderGeometry(0.035, 0.035, 0.42, 7).rotateZ(Math.PI / 2),
  plank: new THREE.BoxGeometry(0.18, 0.022, 0.05),
  // L3 extra tile
  pennant: new THREE.BoxGeometry(0.12, 0.08, 0.01),
  // L4 steam sawmill: a brick mill house with an iron roof, a chimney, a boiler
  // on the old race and a big circular saw on its front.
  steamMill: new THREE.BoxGeometry(0.36, 0.2, 0.2),
  ironRoof: gableRoof(0.18, 0.4),
  chimney: new THREE.CylinderGeometry(0.025, 0.032, 0.42, 6),
  boiler: new THREE.CylinderGeometry(0.045, 0.045, 0.2, 10).rotateZ(Math.PI / 2),
  saw: new THREE.CylinderGeometry(0.07, 0.07, 0.008, 16).rotateX(Math.PI / 2),
  // L5 timber plant: a concrete hall with a glass front and a gantry crane over the log deck.
  plant: new THREE.BoxGeometry(0.4, 0.22, 0.22),
  plantGlass: new THREE.BoxGeometry(0.34, 0.08, 0.01),
  plantStrip: new THREE.BoxGeometry(0.405, 0.014, 0.225),
  plantRoofEdge: new THREE.BoxGeometry(0.41, 0.03, 0.23),
  gantryBeam: new THREE.BoxGeometry(0.5, 0.025, 0.03),
  hook: new THREE.BoxGeometry(0.04, 0.03, 0.04),
  // L4/L5 extra tile: a forwarder (log-carrying machine) and sensor posts.
  forwarderCab: new THREE.BoxGeometry(0.07, 0.07, 0.07),
  forwarderBed: new THREE.BoxGeometry(0.14, 0.02, 0.08),
  forwarderWheel: new THREE.CylinderGeometry(0.026, 0.026, 0.018, 8).rotateX(Math.PI / 2),
  sensorTip: new THREE.OctahedronGeometry(0.022, 0),
  lightPad: new THREE.CylinderGeometry(0.1, 0.1, 0.012, 6),
};

type Spot = { x: number; z: number; s?: number };

function Tree({ x, z, s = 1 }: Spot) {
  return (
    <group position={[x, 0, z]} scale={s}>
      <mesh geometry={G.trunk} material={DARK_WOOD} position={[0, 0.03, 0]} />
      <mesh geometry={G.pine} material={FOREST_PINE} position={[0, 0.04, 0]} />
    </group>
  );
}

function Trees({ spots }: { spots: readonly Spot[] }) {
  return (
    <>
      {spots.map((t) => (
        <Tree key={`${t.x}:${t.z}`} {...t} />
      ))}
    </>
  );
}

function Stumps({ spots }: { spots: readonly Spot[] }) {
  return (
    <>
      {spots.map((p) => (
        <mesh
          key={`${p.x}:${p.z}`}
          geometry={G.stump}
          material={WOOD}
          position={[p.x, 0.025, p.z]}
        />
      ))}
    </>
  );
}

/** Logs lying along x, stacked; `y` is the layer (0 on the ground). */
function LogPile({
  x,
  z,
  logs,
}: {
  x: number;
  z: number;
  logs: readonly { y: number; z: number }[];
}) {
  return (
    <group position={[x, 0, z]}>
      {logs.map((l) => (
        <mesh
          key={`${l.y}:${l.z}`}
          geometry={G.log}
          material={WOOD}
          position={[0, 0.035 + l.y * 0.06, l.z]}
        />
      ))}
    </group>
  );
}

// Trees stand at the back and left, the hut back right; the front right stays
// open for the yard (L2) and the sawmill (L3).
const CAMP_TREES: readonly Spot[] = [
  { x: -0.4, z: -0.2, s: 1.1 },
  { x: -0.12, z: -0.44, s: 0.95 },
  { x: -0.5, z: 0.14, s: 0.8 },
];
const CAMP_STUMPS: readonly Spot[] = [
  { x: -0.2, z: 0.54 },
  { x: -0.04, z: 0.58 },
];
const HUT = { x: 0.4, z: -0.24 };
const PILE = { x: -0.14, z: 0.04 };
// The L1 pile (2 + 1 logs); the yard makes it 3 + 2 + 1.
const PILE_LOGS = [
  { y: 0, z: -0.04 },
  { y: 0, z: 0.04 },
  { y: 1, z: 0 },
];
const MORE_PILE_LOGS = [
  { y: 0, z: 0.12 },
  { y: 1, z: 0.08 },
  { y: 2, z: 0.04 },
];

/** L1: pines, stumps and a log pile beside a hut with a team-coloured roof. */
function Camp({ accent }: ModelProps) {
  return (
    <>
      <Trees spots={CAMP_TREES} />
      <Stumps spots={CAMP_STUMPS} />
      <LogPile {...PILE} logs={PILE_LOGS} />
      <mesh geometry={G.hut} material={DARK_WOOD} position={[HUT.x, 0.075, HUT.z]} />
      <mesh geometry={G.hutRoof} material={accent} position={[HUT.x, 0.2, HUT.z]} />
    </>
  );
}

const SAWHORSE = { x: 0.06, z: -0.2 };
const CART = { x: -0.34, z: 0.36 };
// The yard's pines fill out the stand at the back.
const YARD_TREES: readonly Spot[] = [
  { x: 0.1, z: -0.5, s: 0.9 },
  { x: -0.5, z: -0.42, s: 0.8 },
];
// Front right, in front of where the L3 mill stands.
const SAWPIT = { x: 0.28, z: 0.32 };
// Its posts: the back pair taller, so the roof slopes down to the front.
const SAWPIT_POSTS = [-0.15, 0.15].flatMap((x) => [
  { x, z: -0.1, h: 0.23 },
  { x, z: 0.1, h: 0.17 },
]);
// Sawn planks stacked in the sawpit's right half, lengthwise: 3 layers of 2.
const SAWPIT_PLANKS = [0, 1, 2].flatMap((y) =>
  [0.03, 0.09].map((x) => ({ key: `${y}:${x}`, x: x + (y % 2) * 0.01, y: 0.011 + y * 0.022 }))
);
// Front centre, clear of the log pile, the stumps and the L3 mill.
const PLANKS = { x: -0.04, z: 0.34 };
// A crib of sawn planks, each layer crossing the one below.
const PLANK_LAYERS = Array.from({ length: 10 }, (_, i) => i).flatMap((layer) =>
  [-0.06, 0, 0.06].map((offset) => ({
    key: `${layer}:${offset}`,
    position: [layer % 2 ? offset : 0, 0.011 + layer * 0.024, layer % 2 ? 0 : offset] as [
      number,
      number,
      number,
    ],
    turn: layer % 2 ? Math.PI / 2 : 0,
  }))
);
// The yard's low stack (4 layers); the sawmill stacks the rest on top.
const YARD_PLANK_COUNT = 4 * 3;

function Planks({ layers }: { layers: typeof PLANK_LAYERS }) {
  return (
    <group position={[PLANKS.x, 0, PLANKS.z]}>
      {layers.map((p) => (
        <mesh
          key={p.key}
          geometry={G.plank}
          material={PLANK}
          position={p.position}
          rotation={[0, p.turn, 0]}
        />
      ))}
    </group>
  );
}

/**
 * An open lean-to with a team-coloured roof: on the left a log on trestles over
 * the sawpit, on the right a stack of sawn planks.
 */
function Sawpit({ accent }: { accent: THREE.Material }) {
  return (
    <group position={[SAWPIT.x, 0, SAWPIT.z]}>
      {SAWPIT_POSTS.map((p) => (
        <Pole
          key={`${p.x}:${p.z}`}
          length={p.h}
          position={[p.x, p.h / 2, p.z]}
          material={DARK_WOOD}
        />
      ))}
      <mesh
        geometry={G.pitRoof}
        material={accent}
        position={[0, 0.21, 0]}
        rotation={[0.32, 0, 0]}
      />
      <mesh geometry={G.pit} material={DARK_WOOD} position={[-0.07, 0.006, 0]} />
      <mesh geometry={G.trestle} material={DARK_WOOD} position={[-0.07, 0.025, -0.06]} />
      <mesh geometry={G.trestle} material={DARK_WOOD} position={[-0.07, 0.025, 0.06]} />
      <mesh geometry={G.cartLog} material={WOOD} position={[-0.07, 0.075, 0]} />
      {SAWPIT_PLANKS.map((p) => (
        <mesh
          key={p.key}
          geometry={G.plank}
          material={PLANK}
          position={[p.x, p.y, 0]}
          rotation={[0, Math.PI / 2, 0]}
        />
      ))}
    </group>
  );
}

/**
 * L2: two more pines, a bigger log pile, a sawhorse with a log, a hand cart of
 * logs, a sawpit shed with a team-coloured roof (the L3 mill replaces it) and
 * stacks of light sawn planks.
 */
function Yard({ accent, level }: ModelProps) {
  return (
    <>
      <Trees spots={YARD_TREES} />
      {level < 3 && <Sawpit accent={accent} />}
      <LogPile {...PILE} logs={MORE_PILE_LOGS} />
      <group position={[SAWHORSE.x, 0, SAWHORSE.z]}>
        {[-0.08, 0.08].flatMap((x) =>
          [-0.35, 0.35].map((tilt) => (
            <mesh
              key={`${x}:${tilt}`}
              geometry={G.sawhorseLeg}
              material={DARK_WOOD}
              position={[x, 0.065, tilt * 0.06]}
              rotation={[tilt, 0, 0]}
            />
          ))
        )}
        <mesh geometry={G.sawhorseBar} material={DARK_WOOD} position={[0, 0.13, 0]} />
        <mesh geometry={G.log} material={WOOD} position={[0, 0.175, 0]} />
      </group>
      <group position={[CART.x, 0, CART.z]} rotation={[0, -Math.PI / 2, 0]}>
        <mesh geometry={G.cartBed} material={DARK_WOOD} position={[0, 0.08, -0.02]} />
        <mesh geometry={G.cartWheel} material={WOOD} position={[-0.06, 0.05, -0.03]} />
        <mesh geometry={G.cartWheel} material={WOOD} position={[0.06, 0.05, -0.03]} />
        <mesh geometry={G.cartHandle} material={WOOD} position={[-0.035, 0.07, 0.1]} />
        <mesh geometry={G.cartHandle} material={WOOD} position={[0.035, 0.07, 0.1]} />
        <mesh geometry={G.cartLog} material={WOOD} position={[-0.022, 0.125, -0.02]} />
        <mesh geometry={G.cartLog} material={WOOD} position={[0.022, 0.125, -0.02]} />
      </group>
      <Planks layers={PLANK_LAYERS.slice(0, YARD_PLANK_COUNT)} />
    </>
  );
}

// The mill house stands where the L2 sawpit was (front right), its wheel on the
// front (camera) face over the race; the log deck lies behind it, between the
// sawhorse and the hut.
const MILL = { x: 0.22, z: 0.13 };
const WHEEL = { x: 0.1, y: 0.11, z: 0.155 };
const PADDLES = Array.from({ length: 10 }, (_, i) => (i * Math.PI) / 5);
const DECK = { x: 0.27, z: -0.07 };
// Two logs on the ground, one on top.
const DECK_LOGS = [
  { z: -0.035, y: 0 },
  { z: 0.035, y: 0 },
  { z: 0, y: 1 },
];

/**
 * L3: a sawmill in place of the sawpit — a timber mill house with a
 * team-coloured gable roof, a big light water wheel in a bright millrace in
 * front of it (L4 rebuilds it).
 */
function Sawmill({ accent }: ModelProps) {
  return (
    <group position={[MILL.x, 0, MILL.z]}>
      <mesh geometry={G.millHouse} material={WOOD} position={[0, 0.09, 0]} />
      <mesh geometry={G.millDoor} material={DARK_WOOD} position={[-0.1, 0.05, 0.101]} />
      <mesh geometry={G.millRoof} material={accent} position={[0, 0.265, 0]} />
      <mesh geometry={G.race} material={RACE_WATER} position={[0.06, 0.008, 0.155]} />
      <mesh geometry={G.raceBank} material={PLASTER} position={[0.06, 0.015, 0.215]} />
      <group position={[WHEEL.x, WHEEL.y, WHEEL.z]}>
        <mesh geometry={G.wheel} material={PLANK} />
        <mesh geometry={G.hub} material={DARK_WOOD} />
        {PADDLES.map((a) => (
          <mesh
            key={a}
            geometry={G.paddle}
            material={PLASTER}
            position={[Math.cos(a) * 0.125, Math.sin(a) * 0.125, 0]}
            rotation={[0, 0, a]}
          />
        ))}
      </group>
    </group>
  );
}

/** From L3: a long log deck behind the mill and the yard's plank stack grown into a tall crib. */
function LogDeck(_props: ModelProps) {
  return (
    <>
      <group position={[DECK.x, 0, DECK.z]}>
        {DECK_LOGS.map((l) => (
          <mesh
            key={`${l.y}:${l.z}`}
            geometry={G.deckLog}
            material={WOOD}
            position={[0, 0.035 + l.y * 0.06, l.z]}
          />
        ))}
      </group>
      <Planks layers={PLANK_LAYERS.slice(YARD_PLANK_COUNT)} />
    </>
  );
}

// Chimney behind the mill's right end; the boiler lies on the old race.
const MILL_CHIMNEY = { x: 0.37, z: 0.06 };
const MILL_SMOKE: [number, number, number][] = [[MILL_CHIMNEY.x, 0.44, MILL_CHIMNEY.z]];

/**
 * L4 (Industrial): the sawmill rebuilt in brick under a team roof, a smoking
 * chimney, a steam boiler on the filled-in race, and a big steel circular saw
 * on its front.
 */
function SteamMill({ accent }: ModelProps) {
  return (
    <>
      <group position={[MILL.x, 0, MILL.z]}>
        <mesh geometry={G.steamMill} material={BRICK} position={[0, 0.1, 0]} />
        <mesh geometry={G.millDoor} material={SOOT} position={[-0.1, 0.05, 0.101]} />
        <mesh geometry={G.ironRoof} material={accent} position={[0, 0.285, 0]} />
        <mesh geometry={G.boiler} material={IRON} position={[0.04, 0.045, 0.16]} />
        <mesh geometry={G.saw} material={STEEL} position={[-0.08, 0.1, 0.108]} />
      </group>
      <mesh
        geometry={G.chimney}
        material={DARK_BRICK}
        position={[MILL_CHIMNEY.x, 0.21, MILL_CHIMNEY.z]}
      />
      <Smoke stacks={MILL_SMOKE} />
    </>
  );
}

// The gantry's legs stand either end of the log deck.
const GANTRY_LEGS = [DECK.x - 0.24, DECK.x + 0.24];
const GANTRY_HEIGHT = 0.3;

/**
 * L5 (Modern): a high-tech timber plant — a concrete hall with a glass front,
 * a glowing strip and a team roof edge — and a steel gantry crane over the log deck.
 */
function TimberPlant({ accent, glow }: ModelProps) {
  return (
    <>
      <group position={[MILL.x, 0, MILL.z + 0.02]}>
        <mesh geometry={G.plant} material={CONCRETE} position={[0, 0.11, 0]} />
        <mesh geometry={G.plantGlass} material={GLASS} position={[0, 0.12, 0.111]} />
        <mesh geometry={G.plantStrip} material={glow} position={[0, 0.05, 0]} />
        <mesh geometry={G.plantRoofEdge} material={accent} position={[0, 0.235, 0]} />
      </group>
      {GANTRY_LEGS.map((x) =>
        [-0.07, 0.07].map((dz) => (
          <Beam
            key={`${x}:${dz}`}
            from={[x, 0, DECK.z + dz]}
            to={[x, GANTRY_HEIGHT, DECK.z + dz * 0.4]}
            material={STEEL}
            thickness={0.018}
          />
        ))
      )}
      <mesh geometry={G.gantryBeam} material={accent} position={[DECK.x, GANTRY_HEIGHT, DECK.z]} />
      <Pole
        length={0.1}
        position={[DECK.x + 0.06, GANTRY_HEIGHT - 0.05, DECK.z]}
        material={STEEL}
      />
      <mesh
        geometry={G.hook}
        material={SOOT}
        position={[DECK.x + 0.06, GANTRY_HEIGHT - 0.1, DECK.z]}
      />
    </>
  );
}

type Layer = {
  id: string;
  Layer: ComponentType<ModelProps>;
  /** First level that draws it. */
  from: number;
  /** Last level that draws it (a later layer rebuilds it); absent: every level from `from`. */
  to?: number;
};

function layersAt(layers: readonly Layer[], level: number): readonly Layer[] {
  return layers.filter((l) => level >= l.from && level <= (l.to ?? Number.POSITIVE_INFINITY));
}

/**
 * What each level adds over the earlier levels' layers; a layer with `to`
 * gives way to the one that rebuilds it. A new level is one more entry.
 */
const FORESTRY_LAYERS: readonly Layer[] = [
  { id: 'camp', Layer: Camp, from: 1 },
  { id: 'yard', Layer: Yard, from: 2 },
  { id: 'sawmill', Layer: Sawmill, from: 3, to: 3 },
  { id: 'deck', Layer: LogDeck, from: 3 },
  { id: 'steam-mill', Layer: SteamMill, from: 4, to: 4 },
  { id: 'timber-plant', Layer: TimberPlant, from: 5 },
];

export function Forestry(props: ModelProps) {
  return (
    <>
      {layersAt(FORESTRY_LAYERS, props.level).map(({ id, Layer }) => (
        <Layer key={id} {...props} />
      ))}
    </>
  );
}

// A centre pine ringed by six, 0.38 apart, pointing at the hex corners; the
// gaps between them (towards the hex edges) hold the stumps and the L3 extras.
const GROVE_TREES: readonly Spot[] = [
  { x: 0, z: 0, s: 1 },
  { x: 0, z: -0.38, s: 1.05 },
  { x: 0.33, z: -0.19, s: 0.95 },
  { x: 0.33, z: 0.19, s: 1 },
  { x: 0, z: 0.38, s: 0.95 },
  { x: -0.33, z: 0.19, s: 1.05 },
  { x: -0.33, z: -0.19, s: 0.9 },
];
const GROVE_STUMPS: readonly Spot[] = [
  { x: 0.52, z: 0 },
  { x: -0.26, z: -0.45 },
];

/** L2 extra tile: a grove of full-grown pines and a couple of stumps. */
function Grove(_props: ModelProps) {
  return (
    <>
      <Trees spots={GROVE_TREES} />
      <Stumps spots={GROVE_STUMPS} />
    </>
  );
}

const TILE_PILE_LOGS = [...PILE_LOGS, ...MORE_PILE_LOGS];
const SAPLINGS: readonly Spot[] = [{ x: -0.27, z: 0.47, s: 0.5 }];

/**
 * L3 extra tile, over the grove: a log pile and a sawhorse with a log in the
 * gaps, a sapling, and a marker post flying a team pennant.
 */
function Managed({ accent }: ModelProps) {
  return (
    <>
      <group position={[0.24, 0, 0.41]} rotation={[0, -Math.PI / 3, 0]}>
        <LogPile x={0} z={0} logs={TILE_PILE_LOGS} />
      </group>
      <group position={[-0.48, 0, 0]}>
        {[-0.08, 0.08].flatMap((x) =>
          [-0.35, 0.35].map((tilt) => (
            <mesh
              key={`${x}:${tilt}`}
              geometry={G.sawhorseLeg}
              material={DARK_WOOD}
              position={[x * 0.6, 0.065, tilt * 0.06]}
              rotation={[tilt, 0, 0]}
            />
          ))
        )}
        <mesh
          geometry={G.sawhorseBar}
          material={DARK_WOOD}
          position={[0, 0.13, 0]}
          scale={[0.6, 1, 1]}
        />
        <mesh geometry={G.log} material={WOOD} position={[0, 0.175, 0]} scale={[0.7, 1, 1]} />
      </group>
      <Trees spots={SAPLINGS} />
      <group position={[0.26, 0, -0.45]}>
        <Pole length={0.5} position={[0, 0.25, 0]} material={DARK_WOOD} />
        <mesh geometry={G.pennant} material={accent} position={[0.06, 0.44, 0]} />
      </group>
    </>
  );
}

// L4 plantation: neat staggered rows of pines in place of the grove, clear of the L3 extras.
const PLANTATION_TREES: readonly Spot[] = [
  ...[-0.36, -0.12, 0.12, 0.36].map((x) => ({ x, z: -0.2, s: 0.75 })),
  ...[-0.24, 0, 0.24].map((x) => ({ x, z: 0.1, s: 0.8 })),
];
const FORWARDER = { x: -0.02, z: 0.33 };

/** L4 extra tile: a pine plantation in rows and a red forwarder loaded with logs. */
function Plantation(_props: ModelProps) {
  return (
    <>
      <Trees spots={PLANTATION_TREES} />
      <group position={[FORWARDER.x, 0, FORWARDER.z]}>
        <mesh geometry={G.forwarderCab} material={TRACTOR} position={[0.08, 0.075, 0]} />
        <mesh geometry={G.forwarderBed} material={IRON} position={[-0.04, 0.045, 0]} />
        {[-0.02, 0.02].map((z) => (
          <mesh
            key={z}
            geometry={G.cartLog}
            material={WOOD}
            position={[-0.04, 0.075, z]}
            rotation={[0, Math.PI / 2, 0]}
          />
        ))}
        {[-0.08, 0.08].flatMap((x) =>
          [-0.045, 0.045].map((z) => (
            <mesh
              key={`${x}:${z}`}
              geometry={G.forwarderWheel}
              material={TYRE}
              position={[x, 0.026, z]}
            />
          ))
        )}
      </group>
    </>
  );
}

const SENSORS = [
  { x: 0.52, z: -0.08 },
  { x: -0.5, z: -0.3 },
  { x: 0.5, z: 0.3 },
];

/** L5 extra tile: glowing sensor posts round the plantation and a drone landing pad behind it. */
function Sensors({ glow }: ModelProps) {
  return (
    <>
      {SENSORS.map((p) => (
        <group key={p.x} position={[p.x, 0, p.z]}>
          <Pole length={0.24} position={[0, 0.12, 0]} material={STEEL} />
          <mesh geometry={G.sensorTip} material={glow} position={[0, 0.26, 0]} />
        </group>
      ))}
      <mesh geometry={G.lightPad} material={glow} position={[0, 0.006, -0.5]} />
    </>
  );
}

/**
 * What a grown forestry's extra tile adds at each level over the earlier
 * levels' layers (tiles only exist from L2); a layer with `to` gives way to
 * the one that replaces it.
 */
const TILE_LAYERS: readonly Layer[] = [
  { id: 'grove', Layer: Grove, from: 2, to: 3 },
  { id: 'managed', Layer: Managed, from: 3 },
  { id: 'plantation', Layer: Plantation, from: 4 },
  { id: 'sensors', Layer: Sensors, from: 5 },
];

/** A grown forestry's extra tile. */
export function ForestryTile(props: ModelProps) {
  return (
    <>
      {layersAt(TILE_LAYERS, Math.max(props.level, 2)).map(({ id, Layer }) => (
        <Layer key={id} {...props} />
      ))}
    </>
  );
}
